/**
 * **군수품.** 부대 전체에 쓰는 소모품이다. 상점에서 사 두었다가, 판을 시작하기 전(전투 준비)에 이번 판에 쓸 것을 세
 * 가지까지 고른다. 쓴 것은 그 판을 정산할 때 하나씩 줄어든다. 척후·군량은 Todo5에서 미뤘다가 7단계에 더했다.
 *
 * 플레이한 사람의 Todo5 4단계: 전장 특성(traits.ts)에 대응하려면 장수·동료 몸에 붙는 장비 말고 **부대 전체에 쓰는 것**이
 * 필요하다. 설계는 "세 칸까지 들고 나간다"였다 — 출정 중에는 상점에 갈 수 없으니 사 둔 것이 곧 들고 나간 것이라,
 * 칸을 따로 두지 않고 한 판에 세 가지까지로 줄였다. 기름 먹인 시위(비)·횃불(야습)은 전황이 들어오며 더했다. 척후(예보)와
 * 군량(체력)은 7단계에 더했다 — 설계의 군량은 "판 사이 회복"이었지만 병사의 체력은 판마다 새로 차므로 한 판의 체력으로 옮겼다.
 *
 * **목책 대신 거마.** 처음에는 우리 전열 앞에 막힌 말뚝(바위) 두 줄을 세웠더니 적보다 우리를 더 막았다 — 대형이 틈으로
 * 새어 나가며 흩어져 깊이 17 표준 부대의 생환이 36 → 27명, 대기로 버티다 받아도 기마대 판 이김이 12 → 8/15로 떨어졌다.
 * 화살이 넘어가게도 해 봤는데, 그 판정(`blockedBetweenFp`)이 걸음에도 쓰여 병사가 말뚝을 뚫고 가려다 서 버렸다. 그래서
 * 사람은 지나가고 말만 느린 땅(`Cell.Stakes`)으로 다시 짰다.
 *
 * **전투 판정에 들어가는 값이다.** 명부(`ScenarioConfig.supplies`)에 실리고 문 앞에서 거른다(`sanitizeSupplies`). 그 계정이
 * 실제로 가진 것을 썼는지는 서버의 계정 대조가 본다(`docs/design.md`). 효과를 바꾸면 `REPLAY_VERSION`을 올린다.
 */

export const Supply = {
  /** 우리 궁수 화살 +`ARROW_SUPPLY`. 답: 궁수대·오래 끄는 판. */
  Arrows: 'arrows',
  /** 우리 창의 방패 +`SHIELD_REPAIR_PCT`%. 답: 궁수대. */
  ShieldRepair: 'shieldfix',
  /** 판 처음 `CALTROP_TICKS` 동안 적 기병이 돌격하지 못한다. 답: 기마대. */
  Caltrops: 'caltrops',
  /** 비(`Trait.Rain`)가 와도 우리 궁수의 활이 그대로다. 답: 비. */
  OiledString: 'oiledstring',
  /** 우리 전열 앞에 거마 두 줄(`plantStakes`) — 말이 느리고 돌격하지 못한다. 사람과 화살은 지나간다. 답: 기마대. */
  Stakes: 'stakes',
  /** 야습(`Trait.Night`)에도 우리 궁수가 멀리 쏘고, 화면에서 적이 더 멀리서 보인다. 답: 야습. */
  Torch: 'torch',
  /**
   * 이 판을 치른 뒤 앞의 `SCOUT_AHEAD` 깊이를 안개(`Trait.Fog`) 없이 미리 본다(`RunState.scoutTo`). 판에는 아무것도
   * 바꾸지 않는다 — 결과 화면·갈림길의 예보에 쓰는 것이다. 답: 안개, 갈림길.
   */
  Scout: 'scout',
  /**
   * 우리 병사의 체력이 `RATION_HP_PCT`%. 답: 결사대·오래 끄는 판. 처음에는 기개(사기)를 올렸는데 표준 부대의 병사는 거의
   * 무너지지 않아 결과가 한 판도 바뀌지 않았다.
   */
  Ration: 'ration',
} as const;
export type Supply = (typeof Supply)[keyof typeof Supply];

export interface SupplyDef {
  readonly id: Supply;
  readonly name: string;
  readonly hanja: string;
  /** 하나 값(냥). 한 판의 벌이(깊이 10쯤 600냥)로 두어 가지를 살 수 있게. */
  readonly cost: number;
  /** 무엇을 하는가(한 줄). */
  readonly summary: string;
  /**
   * 효과를 아주 짧게(상점 카드). 상점이 여덟 줄의 긴 목록이라 화면을 많이 먹었다(플레이한 사람이 짚었다) — 카드에는
   * 이것만 적고 온 문장(`summary`)은 카드의 설명과 전투 준비의 고르기 카드에 둔다.
   */
  readonly short: string;
}

export const SUPPLIES: readonly SupplyDef[] = [
  // 화살 보급은 싸다 — 판 하나가 짧으면 화살이 바닥나지 않아 표준 부대로는 차이가 거의 없었다(오래 끄는 판의 것이다).
  { id: Supply.Arrows, name: '화살 보급', hanja: '箭', cost: 80, summary: '우리 궁수의 화살이 15발씩 는다. 오래 끄는 판에 쓴다.', short: '궁수 화살 +15' },
  { id: Supply.ShieldRepair, name: '방패 수리', hanja: '修楯', cost: 150, summary: '우리 창의 방패가 화살을 절반 더 막는다.', short: '창 방패 +50%' },
  { id: Supply.Caltrops, name: '마름쇠', hanja: '鐵菱', cost: 200, summary: '처음 30초 동안 적 기병이 돌격하지 못한다.', short: '30초 동안 적 기병 돌격 막음' },
  { id: Supply.OiledString, name: '기름 먹인 시위', hanja: '油弦', cost: 100, summary: '비가 와도 우리 궁수의 활이 그대로다.', short: '비에도 활이 그대로' },
  { id: Supply.Stakes, name: '거마', hanja: '拒馬', cost: 180, summary: '우리 앞에 말뚝 두 줄. 말은 느리고 돌격하지 못한다. 사람과 화살은 지나간다.', short: '말뚝 두 줄 · 말을 막음' },
  { id: Supply.Torch, name: '횃불', hanja: '炬', cost: 120, summary: '밤에도 우리 궁수가 멀리 쏘고, 적이 더 멀리서 보인다.', short: '밤에도 멀리 쏘고 봄' },
  { id: Supply.Scout, name: '척후', hanja: '斥候', cost: 60, summary: '이 판을 치른 뒤 앞의 세 깊이를 안개 없이 미리 본다.', short: '앞 세 깊이를 미리 봄' },
  { id: Supply.Ration, name: '군량', hanja: '軍糧', cost: 120, summary: '우리 병사가 배불리 먹고 나선다. 체력이 20% 는다.', short: '병사 체력 +20%' },
];

export const SUPPLY_BY_ID: ReadonlyMap<string, SupplyDef> = new Map(SUPPLIES.map((s) => [s.id, s]));

/**
 * 한 가지 군수품을 가질 수 있는 수의 상한. 깨진 저장의 `1e308`은 정수라 받아들여졌고, 하나를 써도 `n - 1`이 같은 수라
 * 줄지 않았다(리뷰가 짚었다). 사기(`buySupply`)도 이 상한에서 멈춘다.
 */
export const SUPPLY_STOCK_MAX = 99;

/** 한 판에 쓰는 군수품 가짓수의 상한. 같은 것을 둘 쓰지는 않는다. */
export const SUPPLY_PER_BATTLE = 3;
/** 척후: 쓴 판의 깊이에서 몇 깊이 앞까지 안개 없이 보는가. */
export const SCOUT_AHEAD = 3;
/** 군량: 우리 병사의 체력을 이 백분율로(장수·동료는 그대로 — 착장과 레벨의 몫이다). */
export const RATION_HP_PCT = 120;
/** 화살 보급: 우리 궁수 한 사람에게 더하는 화살. */
export const ARROW_SUPPLY = 15;
/** 방패 수리: 우리 창 병사의 방패가 막는 수를 이 백분율로(장수·동료의 방패는 그대로다 — 착장이다). */
export const SHIELD_REPAIR_PCT = 150;
/**
 * 마름쇠: 판 처음 이 틱 동안(30틱 = 1초) 적 기병이 돌격하지 못한다. 처음에는 걸음도 60%로 깎았는데, 그러면 적 기병이
 * 먼저 닿지 않고 보병과 한 덩어리로 닿아, 적이 가까워질 때까지 나아가는 대형의 장수가 한 걸음 더 들어가 둘러싸였다
 * (깊이 17 이김 15 → 9/15, 장수 전사 0 → 6). 돌격만 막으니 깊이 18 이김 7 → 10/15(기마대 6 → 9)로 답이 됐다.
 */
export const CALTROP_TICKS = 30 * 30;

/**
 * 믿을 수 없는 군수품 목록을 거른다. 모르는 것·겹친 것은 버리고 `SUPPLIES` 순서로, `SUPPLY_PER_BATTLE`까지 — 리플레이
 * JSON은 아무 목록이나 실어 온다.
 */
export function sanitizeSupplies(raw: readonly unknown[] | null | undefined): Supply[] {
  if (!Array.isArray(raw)) return [];
  const want = new Set(raw.filter((x): x is string => typeof x === 'string'));
  return SUPPLIES.filter((s) => want.has(s.id))
    .map((s) => s.id)
    .slice(0, SUPPLY_PER_BATTLE);
}

export function hasSupply(list: readonly Supply[] | undefined, s: Supply): boolean {
  return list !== undefined && list.includes(s);
}

/** 군수품들의 이름(받은 순서). */
export function supplyNames(list: readonly Supply[] | undefined): string[] {
  if (!list) return [];
  return list.map((s) => SUPPLY_BY_ID.get(s)?.name).filter((n): n is string => n !== undefined);
}
